import React, { useState, useMemo, useCallback, useEffect } from "react";
import moment from "moment";
import { icons } from "constants/icons";
import Pagination from "../../components/Pagination/Pagination";
import CloseIcon from "@mui/icons-material/Close";
import {
  Table,
  Modal,
  Button,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortIcon from "@mui/icons-material/Sort";

interface ProfileInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sports: string[];
  teams: string[];
  teamsString: string;
  gender: string;
  sportPosition: string[];
}

interface Patient {
  _id: string;
  rank?: number;
  imageUrl: string;
  profileInfo: ProfileInfo;
}

interface AthleteData {
  _id: string;
  patient: Patient;
  examStart?: number;
  examEnd?: number;
  calculations: {
    currentAverage: number;
    previousAverage: number;
    averagesDiffPercentage: string;
    hasImproved: boolean;
  };
  percentage?: string;
  lastExam?: string;
}

const AthleteList = ({ athletes }: { athletes: AthleteData[] }) => {
  const tableStyles = {
    container: {
      boxShadow: "0px 2px 4px rgba(49, 29, 29, 0.1)",
      borderRadius: "8px",
      overflow: "hidden",
    },
    headerCell: {
      backgroundColor: "#f5f5f5",
      fontWeight: 600,
      padding: "12px 16px",
      borderBottom: "1px solid #e0e0e0",
      color: "#666",
      whiteSpace: "nowrap",
    },
    headerIcon: {
      verticalAlign: "middle",
      marginLeft: "4px",
      fontSize: "16px",
    },
    cell: {
      padding: "12px 16px",
      borderBottom: "1px solid #e0e0e0",
      whiteSpace: "nowrap",
    },
    nameCell: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    examCell: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    percentage: {
      padding: "4px 8px",
      borderRadius: "4px",
    },
    downArrow: {
      background: "#d32f2f",
    },
    upArrow: {
      background: "#2e7d32",
    },
    tooltipContent: {
      background: "#fff",
      padding: "10px",
      display: "flex",
      flexDirection: "column",
    },
    tooltipItem: {
      background: "#fff",
      color: "#000",
    },
    averageSpan: {
      width: "40px",
    },
  };

  const processedAthletes = useMemo(() => {
    const uniqueAthletes = athletes.reduce((acc, current) => {
      const x = acc.find((item) => item._id === current._id);
      if (!x) {
        return acc.concat([current]);
      }
      return acc;
    }, [] as AthleteData[]);

    return uniqueAthletes;
  }, [athletes]);

  const calculateAge = (dateOfBirth: string) => {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  return (
    <TableContainer component={Paper} style={tableStyles.container}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={tableStyles.headerCell}>
              # <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Name <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Age <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Sport <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Team <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Position <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Gender <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Min Avg of
              <br />
              Last & Previous Exams
            </TableCell>
            <TableCell sx={tableStyles.headerCell}>
              Last Exam <SortIcon style={tableStyles.headerIcon} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processedAthletes.map((athleteData: AthleteData, index: number) => {
            const { patient, lastExam, calculations } = athleteData;
            const { profileInfo, imageUrl } = patient;

            return (
              <TableRow hover>
                <TableCell sx={tableStyles.cell}># {patient?.rank}</TableCell>
                <TableCell sx={tableStyles.cell}>
                  <Box style={tableStyles.nameCell}>
                    <Avatar
                      src={imageUrl}
                      sx={{ width: 24, height: 24 }}
                      alt={`${profileInfo.firstName} ${profileInfo.lastName}`}
                    />
                    {`${profileInfo.firstName || "No"} ${
                      profileInfo.lastName || "Name"
                    } `}
                  </Box>
                </TableCell>
                <TableCell sx={tableStyles.cell}>
                  {Number.isNaN(calculateAge(profileInfo.dateOfBirth))
                    ? "-"
                    : calculateAge(profileInfo.dateOfBirth)}
                </TableCell>
                <TableCell sx={tableStyles.cell}>
                  <Tooltip
                    placement="top"
                    title={
                      <Box sx={tableStyles.tooltipContent}>
                        {profileInfo.sports?.map((sport) => (
                          <Box
                            sx={{ background: "#fff", color: "#000" }}
                            key={sport}
                          >
                            {sport}
                          </Box>
                        ))}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {profileInfo.sports?.length ? profileInfo.sports[0] : "-"}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={tableStyles.cell}>
                  {profileInfo.teamsString || "-"}
                </TableCell>
                <TableCell sx={tableStyles.cell}>
                  <Tooltip
                    placement="top"
                    title={
                      <Box sx={tableStyles.tooltipContent}>
                        {profileInfo.sportPosition?.map((position) => (
                          <Box sx={tableStyles.tooltipItem} key={position}>
                            {position}
                          </Box>
                        ))}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {profileInfo.sportPosition?.length
                        ? profileInfo.sportPosition[0]
                        : "-"}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={tableStyles.cell}>
                  {profileInfo.gender}
                </TableCell>
                <TableCell sx={tableStyles.cell}>
                  <Box style={tableStyles.examCell}>
                    <>
                      <span style={tableStyles.averageSpan}>
                        {calculations?.currentAverage?.toFixed(2)}
                      </span>
                      <span style={tableStyles.averageSpan}>
                        {calculations?.previousAverage?.toFixed(2)}
                      </span>

                      {calculations?.averagesDiffPercentage && (
                        <span
                          style={tableStyles.percentage}
                          className={
                            calculations?.hasImproved
                              ? "bg-green-50 text-green-700"
                              : "bg-red-50 text-red-700"
                          }
                        >
                          {calculations?.averagesDiffPercentage}%
                        </span>
                      )}
                    </>
                  </Box>
                </TableCell>
                <TableCell sx={tableStyles.cell}>
                  {moment(new Date(lastExam || "")).format("MM/DD/YY") || "-"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const AthleteModal = ({
  athletes,
  setOpenFilters,
  customTrigger,
  totalCount,
  withPagination,
}: {
  athletes: any[];
  customTrigger?: any;
  totalCount?: Number;
  setOpenFilters?: any;
  withPagination?: any;
}) => {
  const LIMIT = 15;
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [athleteList, setAthleteList] = useState<any[]>([]);

  useEffect(() => {
    if (!withPagination) {
      setAthleteList(athletes);
    } else {
      setAthleteList(athletes.slice(0, LIMIT));
    }
  }, [athletes, withPagination]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onLoadMore = useCallback(
    (ev, newPageRaw) => {
      setCurrentPage(newPageRaw);
      const list = Array.from(athletes);
      const newPage = newPageRaw - 1;
      const offset = LIMIT * newPage;
      const start = offset;
      const end = offset + LIMIT;
      const result = list.slice(start, end);
      setAthleteList(result);
    },
    [currentPage, athletes]
  );

  const modalStyles = {
    modalContainer: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "98%",
      maxHeight: "90vh",
      bgcolor: "background.paper",
      borderRadius: "8px",
      boxShadow: 24,
      p: 4,
      overflow: "auto",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
    },
    closeButton: {
      position: "absolute",
      right: "8px",
      top: "8px",
    },
  };

  return (
    <div>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {customTrigger ? (
          <div onClick={handleOpen}>{customTrigger} </div>
        ) : (
          <button
            onClick={handleOpen}
            className="flex items-center gap-1 px-4 py-2 bg-sky-400 hover:bg-sky-500 text-white rounded-lg transition-colors"
          >
            <span> See all</span>
          </button>
        )}
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="athlete-modal-title"
        aria-describedby="athlete-modal-description"
      >
        <Box sx={modalStyles.modalContainer}>
          <IconButton
            onClick={handleClose}
            sx={modalStyles.closeButton}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Box sx={modalStyles.header}>
            <Typography
              id="athlete-modal-title"
              variant="h5"
              component="h2"
              style={{
                fontWeight: 600,
                color: "#333",
                display: "flex",
              }}
            >
              Athlete List
              {setOpenFilters && (
                <img
                  aria-hidden="true"
                  src={icons.filterDetail}
                  alt="filterDetail"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setOpenFilters(true);
                  }}
                />
              )}
            </Typography>
          </Box>

          <Box id="athlete-modal-description">
            <AthleteList athletes={athleteList} />
          </Box>
          {totalCount && (
            <div
              style={{
                marginTop: "20px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pagination
                page={currentPage}
                count={Math.ceil(Number(totalCount) / LIMIT)}
                onChange={onLoadMore}
              />
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};
